/**
 * Stage S12 — block environments (`processEnvironments`).
 *
 * Ported from  (cited below as `lc:N`).
 * Spec: `docs/spec/00-MASTER-SPEC.md` §3.10, rules R-280…R-297, plus §3.10.1
 * (`processQuoteWithNverset`) and §3.10.2 (`findInnermostQuoteEnv`).
 *
 * ─── FIDELITY CONTRACT ────────────────────────────────────────────────────────────────────
 * This is a BUG-FOR-BUG port (CLAUDE.md §3, owner decision 2026-07-28). Everything below
 * reproduces the source's observable output exactly, including output that is plainly wrong.
 * The defects deliberately preserved in this file, each pinned by a test in
 * `environments.test.ts`:
 *
 *   DF-3  / RK-04  a row separator written `\\ ` (trailing space) was already destroyed at
 *                  S11, so the rows merge here.               → R-294
 *   DF-4  / RK-05  `\&` became `&amp;` at S11; splitting on `&` then shreds the
 *                  row into `Smith` / `amp; Wesson` / …                  → R-294
 *   DF-7  / G-4    an empty `description` body desynchronises the `i += 2` walk and every
 *                  later label/body pair is mismatched.                  → R-291
 *   DF-9  / G-14   `verbatim` is not verbatim: it runs AFTER S11 and is never escaped, so
 *                  `\textbf{x}` is already `<strong>x</strong>` and raw HTML is emitted.
 *                                                                        → R-287
 *   G-1            a `\citation` starting with `\Nverset` emits an empty leading
 *                  `<blockquote>` that steals `quote-successive-start`.  → §3.10.1
 *   G-5   / R-344  `\begin{tabular}[t]{…}` matches no rule; the whole environment leaks.
 *   G-6   / R-345  `p{3cm}` truncates the format capture at the first `}`; the remainder is
 *                  prepended to the first cell and every column is centred (`c` of `3cm`).
 *   G-7   / R-343  `\cline{a-b}` is not stripped and leaks into the following cell.
 *   G-32  / R-342  `\multicolumn` leaks verbatim in `tabular` but becomes an inert
 *                  `<span data-colspan="N">` in `longtable`.
 *   RK-26          `enumerate` runs after `itemize`, so a nested `enumerate` gains a
 *                  spurious leading empty `<li>`.                        → R-289
 *   R-341          `\item[label]` is not parsed in itemize/enumerate — the brackets render.
 *   C-4            `\Rextrait`/`\Nextrait` PAIR correctly (they arrive here already expanded
 *                  to `\begin{quote}` … `\end{quote}`). Their CONTENT used to carry the
 *                  literal `\verb|<TAB>|` of DF-2; that was fixed at S1b on 2026-08-29, and
 *                  the number now arrives preceded by `\hfill`. The pairing is unchanged.
 *
 * ─── PURITY ───────────────────────────────────────────────────────────────────────────────
 * No I/O, no DOM, no module-level mutable state. The two collaborators this stage needs are
 * injected: the source calls the hoisted `processParagraphs` and `renderMath`
 * directly, which the port cannot do without an import cycle between
 * stages. Behaviour is identical — the same functions, same arguments, same order.
 */

/** Collaborators S12 borrows from other stages. See the purity note above. */
export interface EnvironmentDeps {
  /** S15`processParagraphs`. Used as`processInner` at . */
  processParagraphs: (html: string) => string
  /** KaTeX bridge`renderMath`. Used by`equation`/`align` at , . */
  renderMath: (latex: string, displayMode: boolean) => string
}

/** A resolved `\begin{env}` … `\end{env}` span. `end` is exclusive. */
export interface QuoteEnvMatch {
  start: number
  end: number
  content: string
}

/** As {@link QuoteEnvMatch}, plus the raw `[…]` enumitem options including the brackets. */
export interface ListEnvMatch extends QuoteEnvMatch {
  options: string
}

/** One split section of a quote body: the margin note, and the text that follows it. */
interface QuoteSection {
  hasSmallskip?: boolean
  marginpar?: string
  text: string
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// §3.10.1 — the verse-numbering contract
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Split a quote body at its margin notes so each verse number is emitted OUTSIDE and BEFORE
 * its blockquote.. Spec §3.10.1 — "this implements the hard editorial
 * requirement and must be ported exactly".
 *
 * In Mode A `\Nverset{1}` has already been expanded by the preamble into
 * `\marginpar{\scriptsize 1}` and converted at S11 into
 * `<div class="smallskip"></div><span class="marginpar"> 1</span>`, which is what this
 * function actually matches — the domain classes `.nverset`/`.npar` never appear in
 * production (DF-15).
 *
 * DEFECTS PRESERVED:
 * C-9: the capture is `.trim()`-ed here but NOT in the standalone S11 path,
 *     so the same verse number is `1` inside a quote and ` 1` outside it.
 * C-9: the re-emitted span at  hard-codes `class="marginpar"`, dropping
 *     `tiny-text`/`footnotesize` that `\Npar`/`\Nhidden` put there.
 *   - G-1: a body starting with a margin note leaves `<!--PAR_BREAK-->` as section 0's text.
 *     `'<!--PAR_BREAK-->'.trim()` is truthy, so it counts as a text section, emits an empty
 *     `<blockquote>` and steals ` quote-successive-start` from the first real verse.
 */
export function processQuoteWithNverset(
  content: string,
  className: string,
  deps: EnvironmentDeps,
): string {
  const processInner = deps.processParagraphs

  // R-281 /  — no margin notes at all: wrap and stop. Note this is a *global*
  // regex used with `.match()`, so it returns all matches or null; only nullness matters.
  const nversetPattern = /<span class="marginpar/g
  if (!content.match(nversetPattern)) {
    return `<blockquote class="${className}">${processInner(content)}</blockquote>`
  }

  const sections: QuoteSection[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  // §3.10.1 step 2 /  — verbatim. The optional leading smallskip is captured so it
  // can be re-emitted outside the blockquote; `(?: [^"]+)?` allows extra classes.
  const regex = /(<div class="smallskip"><\/div>)?<span class="marginpar(?: [^"]+)?">([^<]*)<\/span>/g

  // section accumulation.
  while ((match = regex.exec(content)) !== null) {
    const textBefore = content.substring(lastIndex, match.index)
    if (textBefore.trim() || sections.length === 0) {
      if (sections.length === 0) {
        sections.push({ text: textBefore })
      } else {
        const last = sections[sections.length - 1]
        if (last !== undefined && last.text === '') {
          last.text = textBefore
        } else if (last !== undefined) {
          last.text += textBefore
        }
      }
    }
    sections.push({ hasSmallskip: !!match[1], marginpar: (match[2] ?? '').trim(), text: '' })
    lastIndex = match.index + match[0].length
  }

  // trailing text joins the last section.
  const textAfter = content.substring(lastIndex)
  if (textAfter.trim()) {
    const last = sections[sections.length - 1]
    if (sections.length > 0 && last !== undefined) {
      last.text = textAfter
    } else {
      sections.push({ text: textAfter })
    }
  }

  // which sections actually carry text decides the successive-* classes.
  let result = ''
  const textSectionIndices = sections
    .map((s, i) => (s.text.trim() ? i : -1))
    .filter((i) => i !== -1)
  const hasMultipleSections = textSectionIndices.length > 1
  let isFirstBlockquote = true

  // emission.
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i]
    if (section === undefined) continue

    if (section.marginpar) {
      if (section.hasSmallskip) {
        result += '<div class="smallskip"></div>'
      }
      // class hard-coded; extra classes are dropped here (C-9).
      result += `<span class="marginpar">${section.marginpar}</span>`
    } else if (isFirstBlockquote && hasMultipleSections && section.text.trim()) {
      result += '<div class="smallskip"></div>'
    }

    if (section.text.trim()) {
      let successiveClass = ''
      if (hasMultipleSections) {
        if (i === textSectionIndices[0]) {
          successiveClass = ' quote-successive-start'
        } else if (i === textSectionIndices[textSectionIndices.length - 1]) {
          successiveClass = ' quote-successive-end'
        } else {
          successiveClass = ' quote-successive-middle'
        }
      }
      result += `<blockquote class="${className}${successiveClass}">${processInner(
        section.text,
      )}</blockquote>`
      isFirstBlockquote = false
    }
  }

  return result
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// §3.10.2 — pairing
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Find the innermost `\begin{env}` … `\end{env}` span.. Spec §3.10.2.
 *
 * Positional, inside-out, with NO origin tracking and NO balance validation. This is what
 * makes the unbalanced `\Rextrait`/`\Nextrait` pair (pre:200-207) resolve correctly: by the
 * time S12 runs, `\Rextrait` has expanded to a bare `\begin{quote}` and `\Nextrait` to a
 * bare `\end{quote}`, and this function pairs them like any other quote (C-4). An orphan
 * `\begin{quote}` with no `\end` is skipped and does not swallow a later valid pair.
 *
 * PREAMBLE LINK: `\citation` (preambuleV3.tex:345) — `\renewcommand{\citation}[1]{\begin{quote}
 * #1\end{quote}}`. On the live path the macro is expanded at S7 and this function is what turns
 * the result into `<blockquote class="quote-env">`. **An empty argument is part of the
 * vocabulary**: `\citation{}` expands to `\begin{quote}\end{quote}`, which in LaTeX is a valid
 * empty `quote` contributing vertical space, and the Kitáb-i-Aqdas uses it to close a prayer
 * before the next heading.
 *
 * ⚠️ **EMPTY ENVIRONMENTS — the `>=` on the `nextEnd` search is load-bearing.** It was `>`, which
 * demands an `\end` strictly after the content start. An empty environment puts its `\end`
 * EXACTLY at that index, so the pair never matched, `processEnvironments` left it alone, and the
 * raw `\begin{quote}\end{quote}` was published as visible scripture — three times in the live
 * Kitáb-i-Aqdas. Fixed 2026-08-31; {@link findInnermostListEnv} carried the identical off-by-one
 * and was fixed with it. Blast radius measured: 148 fixture conversions unchanged, and the only
 * corpus movement was those three leaks disappearing.
 *
 * The output for an empty environment is an EMPTY `<blockquote>`, not nothing — LaTeX's empty
 * `quote` still contributes vertical space, and `.quote-env`'s margins reproduce it.
 *
 * /!\ NOT fixed here, and pre-existing: the nested-begin check below uses `b.start >
 * begin.contentStart`, so an IMMEDIATELY adjacent nested environment
 * (`\begin{quote}\begin{quote}…`) is not recognised as nested. That behaviour is unchanged by
 * this fix — both comparisons behaved identically for that input before and after.
 */
export function findInnermostQuoteEnv(text: string, envName: string): QuoteEnvMatch | null {
  const beginRegex = new RegExp(`\\\\begin\\{${envName}\\}`, 'g')
  const endRegex = new RegExp(`\\\\end\\{${envName}\\}`, 'g')

  let match: RegExpExecArray | null
  const beginPositions: { start: number; contentStart: number }[] = []

  while ((match = beginRegex.exec(text)) !== null) {
    beginPositions.push({ start: match.index, contentStart: match.index + match[0].length })
  }
  if (beginPositions.length === 0) return null

  const endPositions: number[] = []
  while ((match = endRegex.exec(text)) !== null) {
    endPositions.push(match.index)
  }
  if (endPositions.length === 0) return null

  for (const begin of beginPositions) {
    // `>=`, NOT `>`. An EMPTY environment puts its `\end` exactly AT contentStart, so a strict
    // `>` rejected the pair and the raw `\begin{quote}\end{quote}` fell through to the page as
    // visible text. `\citation{}` expands to precisely that — three occurrences in the live
    // Kitáb-i-Aqdas were publishing it. See the note on this function.
    const nextEnd = endPositions.find((e) => e >= begin.contentStart)
    if (nextEnd === undefined) continue

    const hasNestedBegin = beginPositions.some(
      (b) => b.start > begin.contentStart && b.start < nextEnd,
    )
    if (!hasNestedBegin) {
      const endTag = `\\end{${envName}}`
      return {
        start: begin.start,
        end: nextEnd + endTag.length,
        content: text.substring(begin.contentStart, nextEnd),
      }
    }
  }
  return null
}

/**
 * As {@link findInnermostQuoteEnv}, but also captures an enumitem `[…]` option group.
 *. The option regex is `(\[[^\]]*\])?` — it cannot span a nested `]`.
 */
export function findInnermostListEnv(text: string, envName: string): ListEnvMatch | null {
  // PREAMBLE LINK: \usepackage{enumitem} (preambuleV3.tex:57) — "Composer des énumérations". NOT
  // READ (R-143 deletes every \usepackage), yet the package's `[label=…]` option syntax IS
  // supported: the group captured below feeds getItemizeStyle / getEnumerateStyle. Hard-coded, so
  // editing that preamble line changes nothing.
  // /!\ The preamble's stated requirement at :49-50 — four nested ranks, the fifth unnumbered — is
  // NOT verified anywhere, and RK-26 gives a nested enumerate a spurious leading empty <li>.
  // /!\ `[label=--]` can never match: R-221 rewrote `--` to an en dash at S11 (see getItemizeStyle).
  const beginRegex = new RegExp(`\\\\begin\\{${envName}\\}(\\[[^\\]]*\\])?`, 'g')
  const endRegex = new RegExp(`\\\\end\\{${envName}\\}`, 'g')

  let match: RegExpExecArray | null
  const candidates: { start: number; optionsEnd: number; options: string }[] = []

  while ((match = beginRegex.exec(text)) !== null) {
    candidates.push({
      start: match.index,
      optionsEnd: match.index + match[0].length,
      options: match[1] ?? '',
    })
  }
  if (candidates.length === 0) return null

  const endPositions: number[] = []
  while ((match = endRegex.exec(text)) !== null) {
    endPositions.push(match.index)
  }
  if (endPositions.length === 0) return null

  for (const candidate of candidates) {
    // `>=`, NOT `>` — the same off-by-one fixed in findInnermostQuoteEnv above. An empty list
    // environment put its `\end` exactly at optionsEnd and leaked `\begin{itemize}\end{itemize}`
    // to the page. Fixed with its sibling so the defect does not survive in half the code.
    const nextEnd = endPositions.find((e) => e >= candidate.optionsEnd)
    if (nextEnd === undefined) continue

    const hasNestedBegin = candidates.some(
      (c) => c.start > candidate.optionsEnd && c.start < nextEnd,
    )
    if (!hasNestedBegin) {
      const endTag = `\\end{${envName}}`
      return {
        start: candidate.start,
        end: nextEnd + endTag.length,
        options: candidate.options,
        content: text.substring(candidate.optionsEnd, nextEnd),
      }
    }
  }
  return null
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Lists
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-290 /  — split a list body into `<li>`s.
 *
 * DEFECTS PRESERVED:
 *   - R-341: `\item[a)]` is not parsed. The brackets survive: `<li>[a)] Premier…</li>`.
 *   - RK-26: because itemize is converted before enumerate (R-288 before R-289), a nested
 *     enumerate's body already contains `</li><li>…`. It has no `\item` left to split on, so
 *     the whole string becomes ONE item and the `<li>` prefix produces a leading `<li></li>`.
 */
export function renderListItems(content: string): string {
  const items = content.split(/\\item(?![a-zA-Z])/).filter((s: string) => s.trim())
  return items.map((item: string) => `<li>${item.trim()}</li>`).join('')
}

/**
 * R-289 /  — enumitem `label=` → `list-style-type`.
 *
 * ORACLE NOTE: `includes` is a substring test, so the STARRED enumitem forms do match —
 * `[label=\alph*)]` contains `label=\alph` and yields `lower-alpha`. Spec row R-347 says
 * starred forms have "no rule"; observed output disagrees. Only `label=\arabic` (starred or
 * not) truly has no branch. Verified against the oracle on `lists/04-enumerate-options`.
 */
export function getEnumerateStyle(options: string): string {
  if (!options) return ''
  if (options.includes('label=\\alph') || options.includes('label=(\\alph')) {
    return ' style="list-style-type: lower-alpha;"'
  } else if (options.includes('label=\\Alph') || options.includes('label=(\\Alph')) {
    return ' style="list-style-type: upper-alpha;"'
  } else if (options.includes('label=\\roman') || options.includes('label=(\\roman')) {
    return ' style="list-style-type: lower-roman;"'
  } else if (options.includes('label=\\Roman') || options.includes('label=(\\Roman')) {
    return ' style="list-style-type: upper-roman;"'
  }
  return ''
}

/**
 * R-288 /  — itemize `label=` → `list-style-type`.
 *
 * DEFECT PRESERVED (R-221, pipeline consequence #4): the `--` branch is UNREACHABLE. S11
 *  rewrites `--` to an en dash before S12 ever sees the options, so `[label=--]`
 * arrives here as `[label=–]` and falls through to `''`.
 */
export function getItemizeStyle(options: string): string {
  if (!options) return ''
  if (options.includes('label=')) {
    if (options.includes('--')) return ' style="list-style-type: none;"'
    if (options.includes('\\textbullet')) return ' style="list-style-type: disc;"'
  }
  return ''
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Tables
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * The row/cell engine shared by `tabular` and `longtable`
 *. The two copies in the source are character-for-character identical from
 * the `alignTokens` line onwards; only `longtable`'s pre-clean and its class differ. Sharing
 * them here is a structural change with no observable effect — verified by running both
 * against the original over the fixture corpus and 50,000 randomised inputs.
 *
 * DEFECTS PRESERVED:
 *   - DF-3: rows split on the two-character string `\\`. A separator written `\\ ` was
 *     already collapsed to a single `\` + space by S11, so those rows merge and a
 *     stray `\` lands inside a cell.
 * DF-4: cells split on `&` — including the `&` of the `&amp;` that S11  made
 *     out of `\&`, tearing `Smith \& Wesson` into `Smith` and `amp; Wesson`.
 *   - G-6: alignment comes from the l/c/r letters of the (possibly truncated) format string.
 *   - G-7: only `\hline` is stripped; `\cline{1-2}` stays in the cell text.
 *   - NOT IN ANY SPEC ROW, reported by this port and present in all 9 table fixtures: the
 *     text after the LAST `\\` — typically `\n\hline\n` — passes `filter(r => r.trim())`
 *     because the `\hline` is still there, and only then is `\hline` removed. So every
 *     `\hline`-terminated table ends with a phantom empty row,
 *     `<tr><td class="tabular-align-l"></td></tr>`.
 */
function renderTableBody(format: string, content: string, tableClass: string): string {
  // R-294 /  — alignment letters only; `|` merely selects the class.
  const alignTokens = format.split('').filter((ch: string) => ch === 'l' || ch === 'c' || ch === 'r')
  const hasHline = /\\hline/.test(content)

  // R-294 /  — `'\\\\'` is the two-character LaTeX row separator (DF-3).
  const rowStrings = content
    .trim()
    .split('\\\\')
    .filter((r: string) => r.trim())
  // R-294 /  — `\hline` removed, then split on `&` (DF-4, G-7).
  const allCells = rowStrings.map((row) => {
    const cleaned = row.replace(/\\hline/g, '')
    return cleaned.split('&').map((c: string) => c.trim())
  })

  // R-294 /  — drop exactly two leading empty columns when EVERY row has more
  // than two cells and both of the first two are blank (a LaTeX indentation idiom).
  let leadingEmptyCols = 0
  if (allCells.length > 0) {
    const allStartWithTwoEmpty = allCells.every(
      (cells) => cells.length > 2 && (cells[0] || '').trim() === '' && (cells[1] || '').trim() === '',
    )
    if (allStartWithTwoEmpty) {
      leadingEmptyCols = 2
    }
  }

  let table = `<table class="${tableClass}">`

  // R-294 /  — `th` only on row 0 and only when the table has any `\hline`.
  allCells.forEach((cells, idx) => {
    const visibleCells = cells.slice(leadingEmptyCols)
    const usedAlign = alignTokens.slice(leadingEmptyCols)
    const cellTag = hasHline && idx === 0 ? 'th' : 'td'

    table +=
      '<tr>' +
      visibleCells
        .map((cell: string, i: number) => {
          const a = usedAlign[i] || usedAlign[usedAlign.length - 1] || 'l'
          const alignClass =
            a === 'r' ? 'tabular-align-r' : a === 'c' ? 'tabular-align-c' : 'tabular-align-l'
          return `<${cellTag} class="${alignClass}">${cell}</${cellTag}>`
        })
        .join('') +
      '</tr>'
  })

  table += '</table>'
  return table
}

/**
 * R-294 /  — `tabular`.
 *
 * DEFECTS PRESERVED:
 *   - G-5 / R-344: the regex has no optional-argument group, so `\begin{tabular}[t]{ll}`
 *     matches nothing and the WHOLE environment leaks as literal text.
 *   - G-6 / R-345: `\{([^}]+)\}` stops at the first `}`, so `{|p{3cm}|l|}` captures
 *     `|p{3cm` and the leftover `|l|}` is prepended to the first cell. Alignment is then
 *     read off `|p{3cm` — whose only l/c/r letter is the `c` of `3cm` — so the whole table
 *     is centred.
 *   - G-32 / R-342: `\multicolumn` has no rule here and leaks verbatim.
 */
export function convertTabularEnvironments(html: string): string {
  
  return html.replace(
    /\\begin\{tabular\}\{([^}]+)\}([\s\S]*?)\\end\{tabular\}/g,
    (_m: string, format: string, content: string) => {
      
      const hasVerticalRules = format.includes('|')
      const tableClass = hasVerticalRules ? 'grid-table' : 'plain-table'
      return renderTableBody(format, content, tableClass)
    },
  )
}

/**
 * R-295 /  — `longtable`. Same engine as `tabular` plus a pre-clean.
 *
 * PREAMBLE LINK: preambuleV3.tex declares `\usepackage{longtable}`. That declaration is NOT read
 * here — R-143 deletes every `\usepackage` wholesale, and this handler fires on
 * `\begin{longtable}` alone. The preamble line is kept anyway as the record of intent; do not
 * delete it on the grounds that nothing reads it. Conversely, do not delete this handler on the
 * grounds that no corpus document currently uses `longtable` (0 of 10 as of 2026-08-14) — the
 * document the preamble names, the "Document de référence de la Commission", is not loaded yet.
 * If this handler changes, update the CONVERTISSEUR note on \usepackage{longtable} in
 * preambuleV3.tex.
 *
 * DEFECT PRESERVED — G-32 / R-342: `\multicolumn` is rewritten to
 * `<span data-colspan="N">…</span>` inside an ORDINARY cell. There is no real `colspan`,
 * and `data-colspan` has no CSS or JS consumer anywhere in the app. The same construct in
 * `tabular` leaks verbatim instead: one LaTeX command, two behaviours.
 */
export function convertLongtableEnvironments(html: string): string {
  
  return html.replace(
    /\\begin\{longtable\}\{([^}]+)\}([\s\S]*?)\\end\{longtable\}/g,
    (_m: string, format: string, content: string) => {
      // longtable-only pre-clean. `\caption` is deleted, not rendered.
      const cleanContent = content
        .replace(/\\endhead/g, '')
        .replace(/\\endfirsthead/g, '')
        .replace(/\\endfoot/g, '')
        .replace(/\\endlastfoot/g, '')
        .replace(/\\caption\{[^}]+\}/g, '')
        .replace(
          /\\multicolumn\{(\d+)\}\{[^}]+\}\{([^}]+)\}/g,
          (_n: string, span: string, text: string) => `<span data-colspan="${span}">${text}</span>`,
        )

      
      const hasVerticalRules = format.includes('|')
      const tableClass = hasVerticalRules ? 'grid-table longtable' : 'plain-table longtable'
      return renderTableBody(format, cleanContent, tableClass)
    },
  )
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// The stage
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Stage S12..
 *
 * THE ORDER OF THE 18 STEPS IS LOAD-BEARING and is preserved exactly:
 *
 *   1 abstract · 2 quote · 3 quotation · 4 verse · 5 center · 6 flushleft · 7 flushright ·
 *   8 verbatim · 9 itemize · 10 enumerate · 11 description · 12 equation · 13 align ·
 *   14 tabular · 15 longtable · 16 landscape · 17 figure · 18 minipage
 *
 * Step 9 before step 10 is the whole of RK-26. Steps 2-4 before step 9 mean a list inside a
 * quote is still raw LaTeX when the quote is wrapped, and is converted afterwards in place.
 *
 * Called at S12 — i.e. AFTER S11 `processCommands` and BEFORE S13 (`\\` → `<br>`). That is
 * why the table row splitter still sees `\\` at all (pipeline consequence #9), and why
 * `verbatim` content has already been converted to HTML (G-14 / DF-9).
 */
export function processEnvironments(html: string, deps: EnvironmentDeps): string {
  const processInner = (content: string) => deps.processParagraphs(content)

  // 1 — R-280 /. Hard-coded English "Abstract" in a French document; no
  // processInner, so the body is left for the global S15 pass.
  html = html.replace(
    /\\begin\{abstract\}([\s\S]*?)\\end\{abstract\}/g,
    '<div class="abstract"><div class="abstract-title">Abstract</div>$1</div>',
  )

  // 2 — R-281 /. Inside-out, so nesting yields <blockquote> inside <p> (G-10).
  let innermostQuote: QuoteEnvMatch | null
  while ((innermostQuote = findInnermostQuoteEnv(html, 'quote')) !== null) {
    const replacement = processQuoteWithNverset(innermostQuote.content, 'quote-env', deps)
    html = html.substring(0, innermostQuote.start) + replacement + html.substring(innermostQuote.end)
  }

  // 3 — R-282 /.
  while ((innermostQuote = findInnermostQuoteEnv(html, 'quotation')) !== null) {
    const replacement = processQuoteWithNverset(innermostQuote.content, 'quotation-env', deps)
    html = html.substring(0, innermostQuote.start) + replacement + html.substring(innermostQuote.end)
  }

  // 4 — R-283 /. CSS gives.verse-env `white-space: pre-line`.
  while ((innermostQuote = findInnermostQuoteEnv(html, 'verse')) !== null) {
    const replacement = processQuoteWithNverset(innermostQuote.content, 'verse-env', deps)
    html = html.substring(0, innermostQuote.start) + replacement + html.substring(innermostQuote.end)
  }

  // 5 — R-284 /.
  html = html.replace(
    /\\begin\{center\}([\s\S]*?)\\end\{center\}/g,
    (_m: string, content: string) => `<div class="center-env">${processInner(content)}</div>`,
  )

  // 6 — R-285 /.
  html = html.replace(
    /\\begin\{flushleft\}([\s\S]*?)\\end\{flushleft\}/g,
    (_m: string, content: string) => `<div class="flushleft-env">${processInner(content)}</div>`,
  )
  // 7 — R-286 /.
  html = html.replace(
    /\\begin\{flushright\}([\s\S]*?)\\end\{flushright\}/g,
    (_m: string, content: string) => `<div class="flushright-env">${processInner(content)}</div>`,
  )

  // 8 — R-287 /. DF-9 / G-14: the body arrives already converted by S11 and is never
  // escaped, so `\begin{verbatim}<b>x</b>\end{verbatim}` emits live markup. NOT verbatim.
  html = html.replace(/\\begin\{verbatim\}([\s\S]*?)\\end\{verbatim\}/g, '<pre>$1</pre>')

  // 9 — R-288 /.
  let innermost: ListEnvMatch | null
  while ((innermost = findInnermostListEnv(html, 'itemize')) !== null) {
    const listStyle = getItemizeStyle(innermost.options)
    const listHtml = `<ul${listStyle}>${renderListItems(innermost.content)}</ul>`
    html = html.substring(0, innermost.start) + listHtml + html.substring(innermost.end)
  }

  // 10 — R-289 /. AFTER itemize ⇒ RK-26's spurious leading empty <li>.
  while ((innermost = findInnermostListEnv(html, 'enumerate')) !== null) {
    const listStyle = getEnumerateStyle(innermost.options)
    const listHtml = `<ol${listStyle}>${renderListItems(innermost.content)}</ol>`
    html = html.substring(0, innermost.start) + listHtml + html.substring(innermost.end)
  }

  // 11 — R-291 /. DF-7 / G-4: `split` on a capturing regex yields
  // [pre, label, body, label, body, …]; `.filter(s => s.trim())` DROPS an empty body, so the
  // `i += 2` walk desynchronises and every later label/body pair is mismatched.
  html = html.replace(
    /\\begin\{description\}(\[[^\]]*\])?([\s\S]*?)\\end\{description\}/g,
    (_m: string, _options: string | undefined, content: string) => {
      const items: string[] = content.split(/\\item\[([^\]]+)\]/).filter((s: string) => s.trim())
      let dl = '<dl>'
      for (let i = 0; i < items.length; i += 2) {
        dl += `<dt><strong>${items[i] ?? ''}</strong></dt><dd>${items[i + 1] || ''}</dd>`
      }
      dl += '</dl>'
      return dl
    },
  )

  // 12 — R-292 /. Note the regex accepts a mismatched pair such as
  // `\begin{equation}…\end{equation*}`.
  html = html.replace(
    /\\begin\{equation\*?\}([\s\S]*?)\\end\{equation\*?\}/g,
    (_m: string, math: string) =>
      `<div class="math-display">${deps.renderMath(math.trim(), true)}</div>`,
  )

  // 13 — R-293 /.
  html = html.replace(
    /\\begin\{align\*?\}([\s\S]*?)\\end\{align\*?\}/g,
    (_m: string, math: string) =>
      `<div class="math-display">${deps.renderMath(
        '\\begin{aligned}' + math.trim() + '\\end{aligned}',
        true,
      )}</div>`,
  )

  // 14 — R-294 /.
  html = convertTabularEnvironments(html)

  // 15 — R-295 /.
  html = convertLongtableEnvironments(html)

  // 16 — R-296 /. No processInner.
  // PREAMBLE LINK: \usepackage{pdflscape} (preambuleV3.tex:75), needed for the annexes of the
  // Commission's reference document (:76). NOT READ — R-143 deletes the \usepackage — but the
  // environment is handled anyway, here, and styled by `.landscape` in css.ts. Hard-coded, so
  // editing that preamble line has no effect.
  // /!\ ON SCREEN ONLY. The print half does not work: css.ts's @media print emits
  // `page-orientation: landscape`, which is not a CSS property, so a landscape annexe prints
  // portrait. Doing it properly needs an @page rule, which this converter does not emit.
  html = html.replace(
    /\\begin\{landscape\}([\s\S]*?)\\end\{landscape\}/g,
    '<div class="landscape">$1</div>',
  )

  // 17 — R-297 /. `\includegraphics` has no handler at any stage (R-339), so
  // the figure body is the literal LaTeX command; only `\caption` and `\centering` are
  // consumed. The caption comes from the FIRST `\caption{…}` and `[^}]+` cannot span braces.
  html = html.replace(
    /\\begin\{figure\}(\[[^\]]*\])?([\s\S]*?)\\end\{figure\}/g,
    (_m: string, _pos: string | undefined, content: string) => {
      const captionMatch = content.match(/\\caption\{([^}]+)\}/)
      const caption = captionMatch ? `<figcaption>${captionMatch[1]}</figcaption>` : ''
      const cleanContent = content
        .replace(/\\caption\{[^}]+\}/g, '')
        .replace(/\\centering/g, '')
        .trim()
      return `<figure class="figure-env">${cleanContent}${caption}</figure>`
    },
  )

  // 18 — R-297 /. The raw LaTeX width is injected straight into a style
  // attribute: `\begin{minipage}{0.5\textwidth}` yields `width: 0.5\textwidth`.
  html = html.replace(
    /\\begin\{minipage\}(\[[^\]]*\])?\{([^}]+)\}([\s\S]*?)\\end\{minipage\}/g,
    (_m: string, _valign: string | undefined, width: string, content: string) =>
      `<div class="minipage" style="width: ${width}; display: inline-block; vertical-align: top;">${content}</div>`,
  )

  return html
}
